import { Helmet } from 'react-helmet-async';
import { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Stack,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  InputAdornment,
  Select,
  FormControl,
  InputLabel,
  Tab,
  Tabs,
} from '@mui/material';
import Iconify from '../components/iconify';
import AdminOrganizationsTab from '../components/admin/AdminOrganizationsTab';
import AdminSubscriptionsTab from '../components/admin/AdminSubscriptionsTab';
import * as adminService from '../services/adminService';
import { errorNotification, infoNotification } from '../services/notificationService';

export default function AdminUserManagementPage() {
  // State for user list
  const [users, setUsers] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');

  // Create user dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newConfirmPassword, setNewConfirmPassword] = useState('');
  const [newRole, setNewRole] = useState('USER');
  const [newTier, setNewTier] = useState('beta');

  // Action menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  // Reset password dialog state
  const [resetOpen, setResetOpen] = useState(false);
  const [resetPassword, setResetPassword] = useState('');

  // Change role dialog state
  const [changeRoleOpen, setChangeRoleOpen] = useState(false);

  // Delete confirm dialog state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');

  const [activeTab, setActiveTab] = useState(0);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      const data = await adminService.listUsers(searchDebounced || null, page, rowsPerPage);
      setUsers(data.users);
      setTotalElements(data.totalElements);
    } catch (error) {
      errorNotification('Failed to load users');
    }
  }, [searchDebounced, page, rowsPerPage]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Debounce search (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Create user handler
  const handleCreateUser = async () => {
    if (newPassword !== newConfirmPassword) {
      errorNotification('Passwords do not match');
      return;
    }
    try {
      await adminService.createUser(newUsername, newEmail, newPassword, newRole, newTier);
      infoNotification('User created successfully');
      setCreateOpen(false);
      setNewUsername('');
      setNewEmail('');
      setNewPassword('');
      setNewConfirmPassword('');
      setNewRole('USER');
      setNewTier('beta');
      fetchUsers();
    } catch (error) {
      errorNotification(error.response?.data?.message || 'Failed to create user');
    }
  };

  // Toggle enable/disable
  const handleToggleStatus = async () => {
    if (!selectedUser) return;
    try {
      await adminService.updateUserStatus(selectedUser.id, !selectedUser.enabled);
      infoNotification(`User ${selectedUser.enabled ? 'disabled' : 'enabled'} successfully`);
      setAnchorEl(null);
      fetchUsers();
    } catch (error) {
      errorNotification(error.response?.data?.message || 'Failed to update user status');
    }
  };

  // Change role handler
  const handleChangeRole = async () => {
    if (!selectedUser) return;
    const isCurrentlyAdmin = selectedUser.roles?.includes('ROLE_ADMIN');
    const newRole = isCurrentlyAdmin ? 'USER' : 'ADMIN';
    try {
      await adminService.changeUserRole(selectedUser.id, newRole);
      infoNotification(`User role changed to ${newRole} successfully`);
      setChangeRoleOpen(false);
      setAnchorEl(null);
      fetchUsers();
    } catch (error) {
      errorNotification(error.response?.data?.error || 'Failed to change user role');
    }
  };

  // Reset password handler
  const handleResetPassword = async () => {
    if (!selectedUser) return;
    try {
      await adminService.resetUserPassword(selectedUser.id, resetPassword);
      infoNotification('Password reset successfully');
      setResetOpen(false);
      setResetPassword('');
      setAnchorEl(null);
    } catch (error) {
      errorNotification(error.response?.data?.message || 'Failed to reset password');
    }
  };

  // Delete user handler
  const handleDeleteUser = async () => {
    if (!selectedUser || deleteConfirmName !== selectedUser.username) return;
    try {
      await adminService.deleteUser(selectedUser.id);
      infoNotification('User deleted successfully');
      setDeleteOpen(false);
      setDeleteConfirmName('');
      setAnchorEl(null);
      fetchUsers();
    } catch (error) {
      errorNotification(error.response?.data?.message || 'Failed to delete user');
    }
  };

  const getRoleLabel = (roles) => {
    if (roles?.includes('ROLE_ADMIN')) return 'Admin';
    return 'User';
  };

  const handleOpenMenu = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  return (
    <>
      <Helmet>
        <title>VPSI | {activeTab === 0 ? 'User Management' : 'Organizations'}</title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h4" gutterBottom>
            Administration
          </Typography>
        </Stack>

        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }}>
          <Tab label="Users" />
          <Tab label="Organizations" />
          <Tab label="Subscriptions" />
        </Tabs>

        {activeTab === 0 && (
          <>
            <Stack direction="row" justifyContent="flex-end" spacing={1} mb={2}>
              <Button
                variant="outlined"
                startIcon={<Iconify icon="eva:refresh-fill" />}
                onClick={fetchUsers}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<Iconify icon="eva:plus-fill" />}
                onClick={() => setCreateOpen(true)}
              >
                Create User
              </Button>
            </Stack>

            <Card>
              <Stack sx={{ p: 2 }}>
                <TextField
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search users..."
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ maxWidth: 320 }}
                />
              </Stack>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Username</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Tier</TableCell>
                      <TableCell>Last Sign-In</TableCell>
                      <TableCell>Storage</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id} hover>
                        <TableCell>
                          <Typography variant="subtitle2">{user.username}</Typography>
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{getRoleLabel(user.roles)}</TableCell>
                        <TableCell>
                          {!user.enabled || !user.subscriptionTier ? (
                            <Chip label="Disabled" color="error" size="small" />
                          ) : (
                            <Chip
                              label={user.subscriptionTier.charAt(0).toUpperCase() + user.subscriptionTier.slice(1)}
                              color={
                                { beta: 'info', bronze: 'warning', silver: 'default', gold: 'success' }[
                                  user.subscriptionTier
                                ] || 'default'
                              }
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell>—</TableCell>
                        <TableCell>—</TableCell>
                        <TableCell align="right">
                          <IconButton onClick={(e) => handleOpenMenu(e, user)}>
                            <Iconify icon="eva:more-vertical-fill" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {users.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No users found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[10, 20, 50]}
                component="div"
                count={totalElements}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Card>
          </>
        )}

        {activeTab === 1 && <AdminOrganizationsTab />}

        {activeTab === 2 && <AdminSubscriptionsTab />}
      </Container>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            setChangeRoleOpen(true);
            setAnchorEl(null);
          }}
        >
          {selectedUser?.roles?.includes('ROLE_ADMIN') ? 'Demote to User' : 'Promote to Admin'}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setResetOpen(true);
            setAnchorEl(null);
          }}
        >
          Reset Password
        </MenuItem>
        <MenuItem onClick={handleToggleStatus}>
          {selectedUser?.enabled ? 'Disable' : 'Enable'}
        </MenuItem>
        <MenuItem
          sx={{ color: 'error.main' }}
          onClick={() => {
            setDeleteOpen(true);
            setAnchorEl(null);
          }}
        >
          Delete
        </MenuItem>
      </Menu>

      {/* Create User Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create User</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Username"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              fullWidth
              autoFocus
            />
            <TextField
              label="Email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              fullWidth
            />
            <TextField
              label="Temporary Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
            />
            <TextField
              label="Confirm Password"
              type="password"
              value={newConfirmPassword}
              onChange={(e) => setNewConfirmPassword(e.target.value)}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={newRole}
                label="Role"
                onChange={(e) => setNewRole(e.target.value)}
              >
                <MenuItem value="USER">User</MenuItem>
                <MenuItem value="ADMIN">Admin</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Subscription Tier</InputLabel>
              <Select
                value={newTier}
                label="Subscription Tier"
                onChange={(e) => setNewTier(e.target.value)}
              >
                <MenuItem value="beta">Beta</MenuItem>
                <MenuItem value="bronze">Bronze</MenuItem>
                <MenuItem value="silver">Silver</MenuItem>
                <MenuItem value="gold">Gold</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateUser}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetOpen} onClose={() => setResetOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Reset password for <strong>{selectedUser?.username}</strong>
            </Typography>
            <TextField
              label="New Password"
              type="password"
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              fullWidth
              autoFocus
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleResetPassword} disabled={!resetPassword}>
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Role Confirmation Dialog */}
      <Dialog open={changeRoleOpen} onClose={() => setChangeRoleOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Change User Role</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedUser?.roles?.includes('ROLE_ADMIN')
              ? <>Are you sure you want to demote <strong>{selectedUser?.username}</strong> from Admin to User?</>
              : <>Are you sure you want to promote <strong>{selectedUser?.username}</strong> to Admin?</>}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangeRoleOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleChangeRole}>
            {selectedUser?.roles?.includes('ROLE_ADMIN') ? 'Demote to User' : 'Promote to Admin'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <DialogContentText>
              This action cannot be undone. To confirm, type the username{' '}
              <strong>{selectedUser?.username}</strong> below.
            </DialogContentText>
            <TextField
              label="Username"
              value={deleteConfirmName}
              onChange={(e) => setDeleteConfirmName(e.target.value)}
              fullWidth
              autoFocus
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteUser}
            disabled={deleteConfirmName !== selectedUser?.username}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
